import React from 'react';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * FinancePage component for Revenue Cycle Officers, CEOs, and CFOs
 * Provides executive-level insights and FAQ about AI-powered financial solutions
 */
const FinancePage = () => {
  // FAQ data structure focused on executive concerns
  const faqs = [
    {
      question: "How does AI impact our revenue cycle performance metrics?",
      answer: "Our AI system provides real-time monitoring and optimization of key revenue cycle metrics including Days in AR, Clean Claim Rate, and Collection Rate. It identifies bottlenecks, predicts cash flow trends, and recommends actionable steps to improve financial performance. Executive dashboards provide instant visibility into KPIs and ROI metrics."
    },
    {
      question: "What financial insights can the AI platform deliver?",
      answer: "The platform analyzes historical data, current trends, and market benchmarks to deliver predictive insights on revenue leakage, denial patterns, and optimization opportunities. It provides executive-level reporting on cost reduction opportunities, revenue enhancement strategies, and operational efficiency improvements, directly impacting the bottom line."
    },
    {
      question: "How does the system ensure regulatory compliance?",
      answer: "Our AI continuously monitors changing healthcare regulations and payer requirements, automatically updating validation rules and documentation requirements. It provides audit trails, compliance risk assessments, and proactive alerts on potential compliance issues, helping protect your organization from regulatory penalties and revenue loss."
    },
    {
      question: "What ROI can we expect from implementing the AI solution?",
      answer: "Organizations typically see 15-25% reduction in denial rates, 20-30% improvement in first-pass claim acceptance, and 10-15% reduction in days in AR within the first six months. The system provides detailed ROI tracking, showing direct impact on revenue capture, operational costs, and staff productivity."
    },
    {
      question: "How does the AI solution support strategic decision-making?",
      answer: "The platform provides predictive analytics for strategic planning, including market trend analysis, service line profitability insights, and payer performance comparisons. It enables data-driven decisions about resource allocation, process optimization, and growth opportunities while identifying potential risks and opportunities."
    },
    {
      question: "Can the system integrate with our existing financial workflows?",
      answer: "Yes, our AI platform seamlessly integrates with major EHR systems, financial software, and existing revenue cycle workflows. It enhances rather than replaces your current processes, providing intelligent automation and insights while maintaining familiar workflows for your team."
    }
  ];

  return (
    <div className="finance-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Financial <span style={{ color: '#FFC000' }}>Intelligence</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Empowering healthcare executives with AI-driven insights for
              strategic financial decision-making and revenue cycle optimization.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - FAQ Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Executive Insights
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Executive Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>📈</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Strategic Insights
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Data-driven decision support for strategic planning and resource allocation.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>💰</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Revenue Optimization
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Maximize revenue capture and reduce leakage through intelligent automation.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>⚡</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Operational Efficiency
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Streamline processes and reduce costs while maintaining compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Metrics Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Key Performance Indicators
          </h2>
          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0E3056' }}>25%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduction in Denial Rates</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0E3056' }}>30%</div>
              <p className="text-gray-600 dark:text-gray-300">Improved First-Pass Acceptance</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0E3056' }}>15%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduced Days in AR</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <div className="text-3xl font-bold mb-2" style={{ color: '#0E3056' }}>20%</div>
              <p className="text-gray-600 dark:text-gray-300">Increased Staff Productivity</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to transform your revenue cycle performance?"
        description="Experience how our AI-powered platform can drive financial excellence and strategic growth."
        primaryButtonText="Schedule Executive Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Us"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default FinancePage;